import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from '@/components/ui/Input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/alert';
import { Plus, Edit, Trash2, Save, Loader2, Tag, FolderOpen } from 'lucide-react';
import { useTenant } from '@/lib/tenant/tenant-provider';
import {
  listEvaluationCategories,
  createEvaluationCategory,
  updateEvaluationCategory,
  deleteEvaluationCategory
} from '@/api/performance';
import type {
  EvaluationCategory,
  CreateEvaluationCategoryData,
  UpdateEvaluationCategoryData
} from '@/types/performance';

const CategoriasAvaliacao = () => {
  const { tenantDomain } = useTenant();
  const [categorias, setCategorias] = useState<EvaluationCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState<EvaluationCategory | null>(null);
  const [novaCategoria, setNovaCategoria] = useState<Partial<CreateEvaluationCategoryData>>({
    name: '',
    description: '',
    icon: '',
    order: 1
  });

  // Fetch categories on component mount
  const fetchCategorias = async () => {
    if (!tenantDomain) return;

    setLoading(true);
    setError(null);
    try {
      const result = await listEvaluationCategories(tenantDomain);
      setCategorias(result.items);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao carregar categorias';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategorias();
  }, [tenantDomain]);

  // Auto-hide success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleEditCategoria = (categoria: EvaluationCategory) => {
    setEditingCategoria(categoria);
    setNovaCategoria({
      name: categoria.name,
      description: categoria.description,
      icon: categoria.icon,
      order: categoria.order
    });
    setIsEditDialogOpen(true);
  };

  const handleAddCategoria = () => {
    setEditingCategoria(null);
    setNovaCategoria({
      name: '',
      description: '',
      icon: '',
      order: categorias.length + 1
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveCategoria = async () => {
    if (!tenantDomain) {
      setError('Tenant domain not available');
      return;
    }

    // Validation
    if (!novaCategoria.name?.trim()) {
      setError('Please fill in the category name.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (editingCategoria) {
        // Update existing category
        const updates: UpdateEvaluationCategoryData = {
          name: novaCategoria.name,
          description: novaCategoria.description,
          icon: novaCategoria.icon,
          order: novaCategoria.order
        };
        await updateEvaluationCategory(
          tenantDomain,
          editingCategoria.categoryId,
          updates
        );
        setSuccessMessage('Categoria atualizada com sucesso');
      } else {
        // Create new category
        const data: CreateEvaluationCategoryData = {
          name: novaCategoria.name!,
          description: novaCategoria.description,
          icon: novaCategoria.icon,
          order: novaCategoria.order
        };
        await createEvaluationCategory(tenantDomain, data);
        setSuccessMessage('Categoria criada com sucesso');
      }

      setIsEditDialogOpen(false);
      await fetchCategorias(); // Refresh list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao salvar categoria';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategoria = async (categoryId: string) => {
    if (!tenantDomain) {
      setError('Tenant domain not available');
      return;
    }

    if (!window.confirm('Tem certeza que deseja excluir esta categoria? Isso pode afetar itens de avaliação existentes.')) {
      return;
    }

    setDeleting(categoryId);
    setError(null);
    try {
      await deleteEvaluationCategory(tenantDomain, categoryId);
      setSuccessMessage('Categoria excluída com sucesso');
      await fetchCategorias(); // Refresh list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao excluir categoria';
      // Check for conflict error (category in use)
      if (errorMessage.includes('in use') || errorMessage.includes('409')) {
        setError('Esta categoria não pode ser excluída pois está sendo utilizada por itens de avaliação.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand-gray" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-2xl font-bold">Categorias de Avaliação</h2>
          <p className="text-brand-gray">Configure as categorias para agrupar itens de avaliação</p>
        </div>

        <div className="flex gap-2">
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={handleAddCategoria}
                className="bg-success text-black hover:bg-success"
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Categoria
              </Button>
            </DialogTrigger>

            <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingCategoria ? 'Editar Categoria' : 'Adicionar Nova Categoria'}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    value={novaCategoria.name}
                    onChange={(e) => setNovaCategoria({...novaCategoria, name: e.target.value})}
                    className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                    placeholder="Ex: Liderança, Técnico, Comunicação..."
                  />
                </div>

                <div>
                  <Label htmlFor="description">Descrição</Label>
                  <textarea
                    id="description"
                    value={novaCategoria.description}
                    onChange={(e) => setNovaCategoria({...novaCategoria, description: e.target.value})}
                    className="w-full p-2 bg-brand-dark-lighter border border-brand-gray/20 text-white rounded-md resize-none"
                    rows={3}
                    placeholder="Descreva o que esta categoria representa..."
                  />
                </div>

                <div>
                  <Label htmlFor="order">Ordem de Exibição</Label>
                  <Input
                    id="order"
                    type="number"
                    min="1"
                    value={novaCategoria.order}
                    onChange={(e) => setNovaCategoria({...novaCategoria, order: parseInt(e.target.value) || 1})}
                    className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsEditDialogOpen(false)}
                    className="border-brand-gray/20 text-white hover:bg-brand-dark-lighter"
                    disabled={submitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveCategoria}
                    className="bg-success text-black hover:bg-success"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Salvar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="bg-error/20 border-error">
          <div className="flex items-center gap-2">
            <span className="text-error"></span>
            <div>
              <p className="text-error font-medium">Erro</p>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
        </Alert>
      )}

      {/* Success Alert */}
      {successMessage && (
        <Alert className="bg-success/20 border-success">
          <div className="flex items-center gap-2">
            <span className="text-success"></span>
            <div>
              <p className="text-success font-medium">Sucesso</p>
              <p className="text-green-300 text-sm">{successMessage}</p>
            </div>
          </div>
        </Alert>
      )}

      {/* Categories Table */}
      <Card className="bg-brand-dark-light border-brand-gray/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-3">
            <span>Categorias Configuradas</span>
            <span className="px-2 py-1 bg-brand-dark-lighter rounded-full text-sm font-normal">
              {categorias.length} {categorias.length === 1 ? 'categoria' : 'categorias'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-brand-gray/10">
                <TableHead className="text-white">Nome</TableHead>
                <TableHead className="text-white">Descrição</TableHead>
                <TableHead className="text-white text-center">Ordem</TableHead>
                <TableHead className="text-white text-center">Status</TableHead>
                <TableHead className="text-white text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categorias.sort((a, b) => a.order - b.order).map((categoria) => (
                <TableRow key={categoria.categoryId} className="border-brand-gray/10">
                  <TableCell className="text-white font-medium">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-success" />
                      {categoria.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-white max-w-xs">
                    <div className="truncate" title={categoria.description}>
                      {categoria.description || '-'}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-white">{categoria.order}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={categoria.status === 'active' ? 'success' : 'default'}>
                      {categoria.status === 'active' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditCategoria(categoria)}
                        className="text-brand-gray hover:text-white"
                        disabled={deleting === categoria.categoryId}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteCategoria(categoria.categoryId)}
                        className="text-error hover:text-red-300"
                        disabled={deleting === categoria.categoryId}
                      >
                        {deleting === categoria.categoryId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {categorias.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              {/* Icon Container */}
              <div className="w-16 h-16 bg-success/10 rounded-2xl flex items-center justify-center mb-6">
                <FolderOpen className="h-8 w-8 text-success" />
              </div>

              {/* Heading */}
              <h3 className="text-white text-xl font-display mb-3">
                Configure categorias de avaliação
              </h3>

              {/* Supporting Text */}
              <p className="text-brand-gray text-center max-w-2xl mb-8">
                As categorias agrupam itens de avaliação por tema (ex: Liderança, Técnico, Comunicação).
                Crie categorias para organizar os critérios de avaliação de performance.
              </p>

              {/* Example Visual Box */}
              <div className="bg-brand-dark/50 border border-brand-gray/20 rounded-lg p-6 max-w-2xl w-full mb-6">
                <h4 className="text-white font-medium mb-4">Exemplos de categorias:</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-success" />
                    <span className="text-white">Liderança</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-brand-blue" />
                    <span className="text-white">Técnico</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-warning" />
                    <span className="text-white">Comunicação</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-brand-primary" />
                    <span className="text-white">Trabalho em Equipe</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleAddCategoria}
                  className="bg-success text-black hover:bg-success"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeira Categoria
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CategoriasAvaliacao;
